<script setup>
import { useCouponsStore } from '@/stores/coupons';
import { computed, onBeforeMount, onUnmounted, ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';
import { useI18n } from 'vue-i18n';
import { useRegionsStore } from '@/stores/regions';
import { usePreSportsStore } from '@/stores/pre_sports';
import { useDateTimeSportFiltersStore } from '@/stores/date_time_sport_filters';

const { t } = useI18n();
const router = useRouter();
const couponsStore = useCouponsStore();
const regionsStore = useRegionsStore();
const preSportsStore = usePreSportsStore();
const dateTimeSportFiltersStore = useDateTimeSportFiltersStore();

const selected_option = ref('daily');
const direction = ref(document.dir);
const popup_message = ref(false);

const isShowButtonDisabled = computed(() => {
	if (couponsStore.selectedCompetitionIds.length == 0 || couponsStore.selectedCompetitionIds.length > 40) return true;
	return false;
});

const goToCompetitions = () => {
	if (couponsStore.selectedCompetitionIds.length == 0) return;
	let competition_ids = couponsStore.selectedCompetitionIds.toString();

	router.push({ name: 'pre-sports-time-coupons-competitions', params: { from: dateTimeSportFiltersStore.date_from, sport_id: props.sport_id, competition_ids: competition_ids } });
};

watch(() => couponsStore.selectedCompetitionIds.length, (newValue) => {
	popup_message.value = newValue > 40;
});

const props = defineProps({
	sport_id: {
		type: [Number, String],
		default: null,
	},
});

const splideOptions = ref({
	direction: direction.value,
	pagination: false,
	arrows: false,
	autoWidth: true,
	trimSpace: 'move',
	gap: '0.25rem',
	breakpoints: {
		640: {
			gap: '0.25rem',
		},
	},
});

const formatHourLabel = (slug) => {
	if (!slug) return '';
	return slug.replace(/^next_/, '').replace('_hours', 'h').replace('hours', 'h');
};

const formatDayLabel = (slug) => {
	if (!slug) return '';
	if (slug === 'all') return t('general.all') || 'All';
	if (slug === 'today') return t('days.today') || 'Today';
	if (slug === 'tomorrow') return t('days.tomorrow') || 'Tomorrow';
	if (slug === 'weekend') return t('days.weekend') || 'Weekend';
	let dayName = t('days.' + slug);
	if (dayName && !dayName.startsWith('days.')) return dayName;
	return slug.charAt(0).toUpperCase() + slug.slice(1);
};

const selectDay = (day) => {
	dateTimeSportFiltersStore.selected_option = day.slug;
	dateTimeSportFiltersStore.date_from = day.slug;

	if (day.slug === 'all') {
		regionsStore.fetchRegions(preSportsStore.selected_pre_sport_id).then(() => {
			couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
		});
	} else {
		dateTimeSportFiltersStore.fetchCompetitionsPerRegion().then(() => {
			couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
		});
	}
};

const selectHour = (hour) => {
	dateTimeSportFiltersStore.selected_option = hour.slug;
	dateTimeSportFiltersStore.date_from = hour.slug;

	dateTimeSportFiltersStore.fetchCompetitionsPerRegion().then(() => {
		couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
	});
};

const expand = () => {
	couponsStore.toggleAllExpanded();
};

const clearAllSelections = () => {
	couponsStore.emptyCompetitionsIds();
	dateTimeSportFiltersStore.selected_regions = [];
};

onMounted(() => {
	if (dateTimeSportFiltersStore.selected_option === 'all') {
		regionsStore.fetchRegions(preSportsStore.selected_pre_sport_id).then(() => {
			couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
		});
	} else {
		dateTimeSportFiltersStore.fetchCompetitionsPerRegion().then(() => {
			couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
		});
	}
});

onBeforeMount(() => {


	let hours = ['1_hours', '3_hours', '6_hours', '12_hours', '24_hours', '48_hours'];

	if (hours.includes(dateTimeSportFiltersStore.selected_option)) selected_option.value = 'hours';

	preSportsStore.setSelectedPreSportId(props.sport_id);


	dateTimeSportFiltersStore.date_from = dateTimeSportFiltersStore.selected_option;
	dateTimeSportFiltersStore.fetchUpcomingDaysInfo();


});

const getSelectedCompetionsFixturesString = computed(() => {
	if (couponsStore.selectedCompetitionIds.length > 0) {
		return " (" + couponsStore.getSelectedCompetionsFixturesCount + ")";
	}

	return '';
})

watch(() => props.sport_id, (new_sport_id) => {
	preSportsStore.setSelectedPreSportId(new_sport_id);
	dateTimeSportFiltersStore.fetchUpcomingDaysInfo();

	if (dateTimeSportFiltersStore.date_from !== 'all') {
		dateTimeSportFiltersStore.fetchCompetitionsPerRegion().then(() => {
			couponsStore.pruneSelectedCompetitions(regionsStore.regions_competitions);
		});
	}

});

onUnmounted(() => {
	dateTimeSportFiltersStore.fullyResetStore();
});
</script>

<template>
	<div class="day-time-bar">
		<!-- Left: Mode Switcher (Daily / Hours) -->
		<div class="mode-tab-switcher">
			<button class="mode-tab" :class="{'active': selected_option == 'daily'}" @click="selected_option = 'daily'">
				<span>{{ t('general.daily') }}</span>
				<div v-if="selected_option == 'daily'" class="active-indicator"></div>
			</button>
			<button class="mode-tab" :class="{'active': selected_option == 'hours'}" @click="selected_option = 'hours'">
				<span>{{ t('general.hours') }}</span>
				<div v-if="selected_option == 'hours'" class="active-indicator"></div>
			</button>
		</div>

		<!-- Vertical Divider -->
		<div class="bar-divider"></div>

		<!-- Middle: Capsule Pill Track -->
		<div class="capsule-track-wrapper">
			<div class="capsule-track">
				<Splide v-if="selected_option == 'daily'" :options="splideOptions" class="splide-capsule">
					<SplideSlide v-for="day in dateTimeSportFiltersStore.getUpcomingDaysInfo" :key="day.slug">
						<button class="capsule-pill" :class="{'active': dateTimeSportFiltersStore.selected_option == day.slug}" @click="selectDay(day)">
							<span>{{ formatDayLabel(day.slug) }}</span>
						</button>
					</SplideSlide>
				</Splide>
				<Splide v-else-if="selected_option == 'hours'" :options="splideOptions" class="splide-capsule">
					<SplideSlide v-for="hour in dateTimeSportFiltersStore.upcoming_hours_info" :key="hour.slug">
						<button class="capsule-pill" :class="{'active': dateTimeSportFiltersStore.selected_option == hour.slug}" @click="selectHour(hour)">
							<span>{{ formatHourLabel(hour.slug) }}</span>
						</button>
					</SplideSlide>
				</Splide>
			</div>
		</div>

		<!-- Right Side Actions -->
		<div class="right-actions-group">
			<div class="show-coupons-btn-container" v-if="router.currentRoute.value.name == 'pre-sports-coupons'">
				<button class="show-dropdown-btn" @click="goToCompetitions" :disabled="isShowButtonDisabled">
					<span>{{ $t('general.show_lowercase') + getSelectedCompetionsFixturesString }}</span>
					<svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none" class="chevron-icon">
						<path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</button>
				<div v-if="popup_message" class="tooltip-main left">
					<div class="tooltip-inner">
						<p>{{ $t('general.coupon_tooltip') }}</p>
					</div>
				</div>
			</div>

			<button class="clear-selection-btn" v-if="router.currentRoute.value.name == 'pre-sports-coupons' && couponsStore.selectedCompetitionIds.length > 0" @click="clearAllSelections" title="Clear all selections">
				<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<line x1="18" y1="6" x2="6" y2="18"></line>
					<line x1="6" y1="6" x2="18" y2="18"></line>
				</svg>
			</button>

			<div class="bar-divider" v-if="router.currentRoute.value.name == 'pre-sports-coupons'"></div>

			<button class="filter-action-btn" v-if="router.currentRoute.value.name == 'pre-sports-coupons'" @click="expand" :title="couponsStore.all_expanded ? 'Collapse' : 'Expand'">
				<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="filter-icon">
					<line x1="4" y1="21" x2="4" y2="14"></line>
					<line x1="4" y1="10" x2="4" y2="3"></line>
					<line x1="12" y1="21" x2="12" y2="12"></line>
					<line x1="12" y1="8" x2="12" y2="3"></line>
					<line x1="20" y1="21" x2="20" y2="16"></line>
					<line x1="1" y1="14" x2="7" y2="14"></line>
					<line x1="9" y1="8" x2="15" y2="8"></line>
					<line x1="17" y1="16" x2="23" y2="16"></line>
				</svg>
			</button>
		</div>
	</div>
</template>

<style scoped>
.day-time-bar {
	display: flex;
	align-items: center;
	background-color: var(--dark-gray);
	border: 1px solid var(--tab-gray);
	border-radius: 10px;
	padding: 8px 16px;
	width: 100%;
	gap: 16px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
	margin-bottom: 20px;
	box-sizing: border-box;
}

/* Mode Tab Switcher (Daily / Hours) */
.mode-tab-switcher {
	display: flex;
	align-items: center;
	gap: 20px;
	flex-shrink: 0;
}

.mode-tab {
	background: none;
	border: none;
	padding: 6px 0;
	color: var(--light-gray);
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	position: relative;
	transition: color 0.2s ease;
	outline: none;
}

.mode-tab:hover {
	color: var(--white);
}

.mode-tab.active {
	color: var(--primary);
	font-weight: 600;
}

.mode-tab .active-indicator {
	position: absolute;
	bottom: -4px;
	left: 0;
	width: 100%;
	height: 2px;
	background-color: var(--primary);
	border-radius: 2px;
	box-shadow: 0 0 8px var(--primary);
}

/* Vertical Separator Divider */
.bar-divider {
	width: 1px;
	height: 24px;
	background-color: var(--tab-gray);
	flex-shrink: 0;
}

/* Capsule Track Wrapper */
.capsule-track-wrapper {
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: center;
}

.capsule-track {
	background-color: var(--dark);
	border: 1px solid var(--tab-gray);
	border-radius: 24px;
	padding: 4px;
	width: 100%;
	display: flex;
	align-items: center;
	box-sizing: border-box;
}

.splide-capsule {
	width: 100%;
}

:deep(.splide-capsule .splide__track) {
	padding: 0 !important;
}

:deep(.splide-capsule .splide__arrow) {
	display: none !important;
}

/* Capsule Pill Buttons */
.capsule-pill {
	background: transparent;
	border: none;
	color: var(--off-light-gray);
	font-size: 13px;
	font-weight: 500;
	padding: 7px 18px;
	border-radius: 20px;
	cursor: pointer;
	white-space: nowrap;
	transition: all 0.2s ease-in-out;
	outline: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.capsule-pill span {
	color: var(--off-light-gray);
	font-size: 13px;
	font-weight: 500;
	line-height: 1;
	display: inline-block;
	transition: color 0.2s ease;
}

.capsule-pill:hover {
	background: var(--light-dark);
}

.capsule-pill:hover span {
	color: var(--white) !important;
}

.capsule-pill.active {
	background-color: var(--primary) !important;
	border-color: var(--primary) !important;
	box-shadow: 0 2px 10px rgba(99, 162, 70, 0.4);
}

.capsule-pill.active span {
	color: var(--white) !important;
	font-weight: 700 !important;
}

/* Right Actions Group */
.right-actions-group {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-shrink: 0;
}

.show-coupons-btn-container {
	position: relative;
}

.show-dropdown-btn {
	background-color: var(--light-dark);
	border: 1px solid var(--tab-gray);
	border-radius: 8px;
	padding: 7px 14px;
	color: var(--white);
	font-size: 13px;
	font-weight: 500;
	display: inline-flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	outline: none;
}

.show-dropdown-btn:hover:not(:disabled) {
	background-color: var(--primary);
	border-color: var(--primary);
	color: var(--white);
}

.show-dropdown-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
	border-color: var(--tab-gray);
}

.show-dropdown-btn .chevron-icon {
	transition: transform 0.2s ease;
	color: var(--off-light-gray);
}

.show-dropdown-btn:hover:not(:disabled) .chevron-icon {
	color: var(--white);
}

.filter-action-btn {
	background: transparent;
	border: none;
	color: var(--off-light-gray);
	padding: 6px;
	border-radius: 6px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;
	outline: none;
}

.filter-action-btn:hover {
	color: var(--white);
	background-color: var(--light-dark);
}

.clear-selection-btn {
	background: transparent;
	border: none;
	color: var(--off-light-gray);
	padding: 6px;
	border-radius: 6px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.2s ease;
	outline: none;
}

.clear-selection-btn:hover {
	color: #ff5c5c;
	background-color: rgba(255, 92, 92, 0.12);
}

/* RTL Support */
html[dir="rtl"] .day-time-bar {
	direction: rtl;
}

html[dir="rtl"] .mode-tab-switcher {
	gap: 20px;
}


</style>